import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, type ArrivalAnnotation } from '@/shared/api';
import { AnnotationOverlay, nextAnnotation, type AnnotationTool } from '@/shared/annotations';
import { cn } from '@/shared/lib/cn';

interface Props {
  stepId: string;
  imageId: string;
  variant: 'thumb' | 'full';
  annotations: ArrivalAnnotation[];
  className?: string;
  /** Gesetzt = Zeichenmodus: eine Zieh-Geste erzeugt eine Markierung. */
  draw?: { tool: AnnotationTool; color: string; onAdd: (annotation: ArrivalAnnotation) => void };
  onClick?: () => void;
}

/**
 * Bild mit Markierungs-Overlay. Das Bild kommt als Blob über den authentifizierten Aufruf —
 * ein `<img src>` kann keinen Bearer-Header mitschicken.
 */
export function ArrivalImageCanvas(props: Props) {
  const { stepId, imageId, variant, annotations, className, draw, onClick } = props;
  const api = useApi();
  const frame = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const blob = useQuery({
    queryKey: ['arrival-steps', stepId, 'images', imageId, variant],
    queryFn: () => api.arrivalSteps.images.content(stepId, imageId, variant),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!blob.data) return;
    const url = URL.createObjectURL(blob.data);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob.data]);

  const relative = (event: { clientX: number; clientY: number }) => {
    const box = frame.current?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return { x: 0, y: 0 };
    return { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.height };
  };

  return (
    <div
      ref={frame}
      className={cn('bg-card-alt relative overflow-hidden rounded-md', className)}
      onClick={onClick}
      onPointerDown={draw ? (event) => (start.current = relative(event)) : undefined}
      onPointerUp={
        draw
          ? (event) => {
              const from = start.current;
              start.current = null;
              if (!from) return;
              draw.onAdd(nextAnnotation(draw.tool, draw.color, from, relative(event)));
            }
          : undefined
      }
    >
      {objectUrl ? (
        <img src={objectUrl} alt="" className="block h-full w-full object-cover" loading="lazy" />
      ) : (
        <div className="h-full w-full animate-pulse" />
      )}
      <AnnotationOverlay annotations={annotations} />
    </div>
  );
}
